import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, Download, AlertTriangle, Check } from 'lucide-react';
import { useState, useEffect } from 'react';

interface SSHKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  privateKey: string;
  taskId: string;
}

const SSHKeyModal = ({ isOpen, onClose, privateKey, taskId }: SSHKeyModalProps) => {
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  // Auto-download when modal opens
  useEffect(() => {
    if (isOpen && privateKey && !downloaded) {
      handleDownload();
    }
  }, [isOpen, privateKey]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(privateKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([privateKey], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gpu-instance-${taskId}.pem`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloaded(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-yellow-500" />
            SSH Private Key
          </DialogTitle>
          <DialogDescription>
            This is your SSH private key. Save it now - it will not be shown again!
          </DialogDescription>
        </DialogHeader>

        <Alert variant="warning" className="border-yellow-500 bg-yellow-50">
          <AlertTriangle className="size-4" />
          <AlertDescription>
            <strong>Important:</strong> This private key is shown only once. Make sure to
            download or copy it before closing this dialog. You will not be able to
            retrieve it later.
          </AlertDescription>
        </Alert>

        <div className="relative">
          <pre className="bg-muted p-4 rounded-lg text-xs overflow-auto max-h-[300px] font-mono">
            {privateKey}
          </pre>
          <div className="absolute top-2 right-2 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleCopy}
              className="h-8"
            >
              {copied ? (
                <Check className="size-4 mr-1" />
              ) : (
                <Copy className="size-4 mr-1" />
              )}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          <p className="mb-2">After downloading, set the correct permissions:</p>
          <code className="bg-muted px-2 py-1 rounded text-xs">
            chmod 400 gpu-instance-{taskId}.pem
          </code>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleDownload}>
            <Download className="size-4 mr-2" />
            Download .pem
          </Button>
          <Button onClick={onClose}>
            {downloaded ? "I've saved my key" : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SSHKeyModal;
